import { Controller } from '@hotwired/stimulus'

import $ from 'jquery';
import { find, filter, forEach, includes, map, max, reject, sortBy, template, uniqBy } from 'lodash';
import { asyncFetchData, asyncPaginatedFetch } from '../../utilities/http-utilities'
import EventPublisher from '../../utilities/event_publisher'
import LayoutPosition from '../../models/layout_position'
import LayoutSection from '../../models/layout_section'
import Landmark from '../../models/landmark'

export default class extends Controller {
  static targets = [
    'container',
    'landmarks',
    'landmarkTemplate',
    'landmarkLabels',
    'landmarkLabelTemplate',
    'layout',
    'layoutContainer',
    'layoutLoading',
    'layoutOuterContainer',
    'layoutPositions',
    'layoutSections',
    'positions',
    'positionTemplate',
    'sectionSelect',
    'circleTemplate',
    'diamondTemplate',
    'squareTemplate',
    'triangleTemplate',
  ];

  connect() {
    this.POSITION_SIZE_PX = 70;
    this.LAYOUT_VERT_PADDING = 10;
    this.pageRequestSize = 25;
    this.fetchHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    }

    this.orderId = this.containerTarget?.dataset?.orderId;
    this.destinationId = this.containerTarget?.dataset?.destinationId;
    this.positionTemplate = template(this.positionTemplateTarget.innerHTML)
    this.landmarkTemplate = template(this.landmarkTemplateTarget.innerHTML)
    this.landmarkLabelTemplate = template(this.landmarkLabelTemplateTarget.innerHTML)
    this.triangleTemplate = template(this.triangleTemplateTarget.innerHTML)
    this.circleTemplate = template(this.circleTemplateTarget.innerHTML)
    this.squareTemplate = template(this.squareTemplateTarget.innerHTML)
    this.diamondTemplate = template(this.diamondTemplateTarget.innerHTML)
    this.resetForm()

    document.addEventListener('productsLoaded', this.onProductsLoaded, true);
    document.addEventListener('productSelect', this.onProductSelect)
    document.addEventListener('scheduleContinue', this.onScheduleContinue, true);
    document.addEventListener('destinationSelect', this.onDestinationSelect);
    document.addEventListener('cartAddSuccess', this.onCartAddSuccess);
  }

  resetForm() {
    this.layoutPositionsTarget.innerHTML = ''
    this.positionsTarget.innerHTML = ''
    this.landmarksTarget.innerHTML = ''
    this.landmarkLabelsTarget.innerHTML = ''
    this.products = [];
    this.availabilities = [];
    this.inventoryUnitIds = [];
    this.groupedAvails = {};
    this.layoutUnitPositions = [];
    this.layoutPositions = [];
    this.currentSelectionGroup = '';
    this.inventory_unit_ids_available_for_all_availabilities = [];
  }

  onProductsLoaded = event => {
    this.products = event?.detail?.products
  }

  onDestinationSelect = event => {
    this.resetForm()
    this.destinationId = event?.detail?.destinationId

    if (!this.destinationId) {
      this.destination = null
      return
    }

    if (this.destination?.id !== this.destinationId) {
      this.getDestination(this.destinationId)
    }
  }

  async getDestination() {
    const destination = await asyncFetchData(`/sandbar/admin/destinations/${this.destinationId}.json`)
    this.destination = destination
    EventPublisher.publish('destinationLoaded', { destination })
  }

  onProductSelect = event => {
    this.resetForm()
    this.product = event.detail.product
    this.productId = this.product?.id
  }

  onScheduleContinue = event => {
    this.startDate = event?.detail?.startDate
    this.endDate = event?.detail?.endDate
    this.availabilities = event?.detail?.availabilities
    this.availabilityIds = event?.detail?.availabilityIds
    this.groupedAvails = event.detail?.groupedAvails
    this.inventory_unit_ids_available_for_all_availabilities = event.detail?.inventory_unit_ids_available_for_all_availabilities
    if (this.product?.layout) {
      if (this.destination?.has_migrated_layout) {
        this.getV3Positions().then(positions => {
          this.layoutPositions = positions.map(position => LayoutPosition.fromAttributes(position))
          this.buildV3Layout()
        })
          .catch(err => this.handleError(err))
        this.getLandmarks().then(landmarks => {
          this.landmarks = landmarks.map(landmark => Landmark.fromAttributes(landmark))
          this.addLandmarksToLayout()
        })
          .catch(err => this.handleError(err))
        this.getV3Sections().then(sections => {
          const unsortedSections = sections.map(section => LayoutSection.fromAttributes(section))
          this.layoutSections = sortBy(unsortedSections, ['sortOrder', 'identifier'])
          this.addSectionSelection()
        })
          .catch(err => this.handleError(err))

      } else {
        this.enableLoading()
        this.getLayoutPositions(1)
      }
    }
  }

  onCartAddSuccess = () => {
    this.resetForm();
  }

  enableLoading() {
    this.sectionSelectTarget.classList.add('hidden');
    this.layoutContainerTarget.classList.add('hidden');
    this.layoutLoadingTarget.classList.remove('hidden');
  }

  stopLoading() {
    this.layoutLoadingTarget.classList.add('hidden');
    this.layoutContainerTarget.classList.remove('hidden');
    if (this.layoutSections.length > 1) {
      this.sectionSelectTarget.classList.remove('hidden');
    }
  }

  choosePosition = event => {
    const target = event.currentTarget;
    const targetInventoryUnit = target.dataset.inventoryUnitId

    if (includes(target.classList, 'selected')) {
      target.classList.remove('selected');
      this.inventoryUnitIds = reject(this.inventoryUnitIds, inventoryUnitId => inventoryUnitId === targetInventoryUnit);
    } else {
      this.inventoryUnitIds.push(targetInventoryUnit);
      target.classList.add('selected');
    }

    const detail = { inventoryUnitIds: this.inventoryUnitIds }
    let positionSelectedEvent = new CustomEvent('positionSelected', { detail });
    document.dispatchEvent(positionSelectedEvent);
  }

  getLayoutPositions = page => {
    let pageNum = page || 1
    const path = '/sandbar/admin/layout_unit_positions'
    fetch(
      `${path}?start_date=${this.startDate}&destination_id=${this.destinationId}&page=${pageNum}&limit=${this.pageRequestSize}`,
      {
        headers: this.fetchHeaders,
        method: 'GET'
      }
    )
      .then(resp => {
        if (resp.ok) {
          return resp.json();
        } else {
          resp.json().then(err => this.handleError(err))
        }
      })
      .then(layoutPositions => {
        layoutPositions.forEach(position => {
          position.shape = this.getProductShape(position.product_id)
        })
        this.layoutUnitPositions = [...this.layoutUnitPositions, ...layoutPositions]

        if (layoutPositions.length === this.pageRequestSize) {
          // NOTE: recursive for pagination!
          this.getLayoutPositions(pageNum + 1);
        } else {
          this.layoutUnitPositions = this.layoutUnitPositions.sort((a, b) => a.position_y - b.position_y || a.position_x - b.position_x);

          this.findLayoutSections();
          this.buildLayout(this.layoutUnitPositions);
          this.stopLoading()
        }
      }).catch(err => this.handleError(err));

  }

  async getV3Positions() {
    const url = `/sandbar/admin/layout_positions?start_date=${this.startDate}&end_date=${this.endDate}&destination_id=${this.destinationId}`
    return await asyncPaginatedFetch(url)
  }

  async getLandmarks() {
    const url = `/sandbar/admin/landmarks?start_date=${this.startDate}&end_date=${this.endDate}&destination_id=${this.destinationId}`
    return await asyncPaginatedFetch(url)
  }

  async getV3Sections() {
    const url = `/sandbar/admin/layout_sections?start_date=${this.startDate}&end_date=${this.endDate}&destination_id=${this.destinationId}`
    return await asyncPaginatedFetch(url)
  }

  findLayoutSections() {
    this.layoutSections = map(uniqBy(this.layoutUnitPositions, 'layout_section_name'), 'layout_section_name');
    this.layoutSectionsTarget.innerHTML = '';
    forEach(this.layoutSections, section => {
      let option = new Option(section, section);
      this.layoutSectionsTarget.append(option);
    });
    this.selectedLayoutSection = this.layoutSections[0];
  }

  buildLayout = () => {
    const layoutPositions = filter(this.layoutUnitPositions, ['layout_section_name', this.selectedLayoutSection]);
    this.layoutPositionsTarget.innerHTML = '' // reset if destination or section changes.
    let currentRowElement = $('<div class="py-2 flex flex-row"></div>').appendTo(this.layoutPositionsTarget);
    let maxColumn = Math.max.apply(Math, map(layoutPositions, 'position_x'));
    let maxRow = Math.max.apply(Math, map(layoutPositions, 'position_y'));
    let row = 0;

    for (let i = 0; i <= maxRow; i += 1) {
      for (let j = 0; j <= maxColumn; j += 1) {
        const currentUnitPos = find(layoutPositions, { position_x: j, position_y: i });
        if (!currentUnitPos) {
          continue;
        }

        if (currentUnitPos && currentUnitPos.position_y !== row) {
          currentRowElement = $('<div class="py-2 flex flex-row"></div>').appendTo(this.layoutPositionsTarget);
          row = currentUnitPos.position_y;
        }

        if (!currentUnitPos.product_id) {
          currentRowElement.append(`<div class="float-left w-12 mr-2 text-center">
            <div class="inventory-unit h-12 w-12 inline-block"></div>
            </div>`);
        } else {
          const label = this.getPositionIdentifier(currentUnitPos, i, j)
          const unit_id = currentUnitPos.inventory_unit_id
          const enabled = currentUnitPos.product_id === this.productId && this.unitAvailableForAllSelections(currentUnitPos.inventory_unit_id)
          const shape = currentUnitPos.shape

          currentRowElement.append(`<div class="flex flex-col flex-nowrap items-center w-12 mr-2">
              <label>${ label }</label>
              <div ${ enabled ? '' : 'disabled="true"' }
                id="layout-inventory-unit-id-${ unit_id }"
                class="${ shape } inventory-unit h-12 w-12"
                data-action="click->order--layout#choosePosition"
                data-inventory-unit-id="${ unit_id }"
              >
            </div>`);
        }
      }
    }
  }

  get positionMaxY() {
    return max(map(this.layoutPositions, 'y')) || 0
  }
  get landmarkMaxY() {
    return max(map(this.landmarks, l => l.topLeftY + l.height)) || 0
  }
  get layoutHeight() {
    const yMax = max([this.positionMaxY, this.landmarkMaxY]) || 0
    return yMax * this.POSITION_SIZE_PX
  }

  setLayoutDimensions = () => {
    const height = this.layoutHeight
    this.layoutTarget.style.width = '100%'
    this.layoutTarget.style.height = `${height}px`
    this.layoutContainerTarget.style.height = `${height + (2 * this.LAYOUT_VERT_PADDING)}px`
    this.layoutContainerTarget.style.width = '100%'
    this.layoutContainerTarget.style.paddingTop = `${this.LAYOUT_VERT_PADDING}px`
    this.layoutContainerTarget.style.paddingBottom = `${this.LAYOUT_VERT_PADDING}px`
  }

  buildV3Layout = () => {
    this.setLayoutDimensions()
    const wrapperLength = this.POSITION_SIZE_PX

    this.layoutPositions.forEach(position => {
      if (position.active === false) {
        return
      }

      const shapeStyle = position.highlight ? `stroke: ${position.highlight};` : ''
      let shapeHTML
      switch (position.shape) {
      case 'triangle':
        shapeHTML = this.triangleTemplate({ shapeStyle })
        break
      case 'square':
        shapeHTML = this.squareTemplate({ shapeStyle })
        break
      case 'diamond':
        shapeHTML = this.diamondTemplate({ shapeStyle })
        break
      default:
        shapeHTML = this.circleTemplate({ shapeStyle })
      }

      const attrs = {
        classes: 'inventory-unit disabled',
        top: `${position.y * this.POSITION_SIZE_PX}px`,
        left: `${position.x * this.POSITION_SIZE_PX}px`,
        label: position.identifier,
        inventoryUnitId: position.inventoryUnitId,
        action: '',
        wrapperLength,
        shapeHTML,
      }

      const enabled = (this.product.name === position.productName) &&
        this.unitAvailableForAllSelections(position.inventoryUnitId)
      if (enabled) {
        attrs.action = 'click->order--layout#choosePosition'
        attrs.classes = 'inventory-unit'
      }
      const positionHTML = this.positionTemplate(attrs)
      this.positionsTarget.insertAdjacentHTML('beforeend', positionHTML)
    })
  }

  addLandmarksToLayout = () => {
    if (!this.landmarks) {
      return;
    }
    this.landmarks.forEach(landmark => {
      let { rotation, svgURL, label, filename, key } = landmark

      const attrs = {
        rotation, svgURL, label, filename, key,
        width: landmark.width * this.POSITION_SIZE_PX,
        height: landmark.height * this.POSITION_SIZE_PX,
        topLeftX: landmark.topLeftX * this.POSITION_SIZE_PX,
        topLeftY: landmark.topLeftY * this.POSITION_SIZE_PX,
      }

      const landmarkHTML = this.landmarkTemplate(attrs)
      this.landmarksTarget.insertAdjacentHTML('beforeend', landmarkHTML)
      // labels in a sibling div for new z-index stacking context (landmarks -> positions -> landmark labels)
      const landmarkLabelHTML = this.landmarkLabelTemplate(attrs)
      this.landmarkLabelsTarget.insertAdjacentHTML('beforeend', landmarkLabelHTML)
    })
  }

  addSectionSelection = () => {
    this.sectionSelectTarget.classList.add('hidden');
    this.layoutSectionsTarget.innerHTML = '';
    this.selectedLayoutSection = null

    if (this.layoutSections.length > 0) {
      const defaultOption = new Option('All', null);
      this.layoutSectionsTarget.append(defaultOption);

      forEach(this.layoutSections, section => {
        const option = new Option(section.identifier, section.id);
        this.layoutSectionsTarget.append(option);
      });

      this.sectionSelectTarget.classList.remove('hidden');
    }
  }

  unitAvailableForAllSelections(inventoryUnitId) {
    return this.inventory_unit_ids_available_for_all_availabilities.includes(inventoryUnitId)
  }

  getPositionIdentifier(pos, row, col) {
    if (pos.position_identifier) {
      return pos.position_identifier;
    } else {
      return `${String.fromCharCode(97 + row).toUpperCase()}-${col + 1}`
    }
  }

  changeLayoutSection = event => {
    event.preventDefault();

    if (this.layoutUnitPositions.length > 0) { // NOTE: old layout style
      this.inventoryUnitIds = [];
      this.selectedLayoutSection = event.target.value;
      this.buildLayout(this.layoutUnitPositions);
    } else if (this.layoutPositions.length > 0) {
      const selectedSectionId = event.target.value
      const section = find(this.layoutSections, ['id', parseInt(selectedSectionId)])
      section.hydratePositions(this.layoutPositions)
      const scrollX = section.minX * this.POSITION_SIZE_PX
      const scrollY = section.minY * this.POSITION_SIZE_PX
      this.layoutContainerTarget.scroll({ top: scrollY, left: scrollX, behavior: 'smooth' })
    }
  }

  getProductShape(productId) {
    let currentProduct = find(this.products, { id: productId })
    return currentProduct?.meta?.shape || 'circle';
  }

  handleError(error) {
    console.error('error: ', error);
  }
}
